import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { where } from 'firebase/firestore';
import type { StoryRunDoc } from '@spirits/shared';
import { useGameStore } from '../store/useGameStore';
import { useCollection } from '../hooks/useCollection';
import { AppHeader } from '../lobby/AppHeader';

export default function PublicRunsRoute() {
  const user = useGameStore((s) => s.user);
  if (!user) {
    return (
      <div className="h-screen flex items-center justify-center text-slate-400">
        <p className="text-xl tracking-wider">connecting to the unseen…</p>
      </div>
    );
  }
  return <PublicRunsAuthed />;
}

function PublicRunsAuthed() {
  const { data: runs, loading } = useCollection<StoryRunDoc>('stories', where('visibility', '==', 'public'));

  const active = useMemo(
    () =>
      [...runs]
        .filter((r) => r.status === 'active')
        .sort((a, b) => tsMillis(b.updatedAt) - tsMillis(a.updatedAt)),
    [runs],
  );

  return (
    <div className="min-h-screen flex flex-col bg-slate-950 text-slate-100">
      <AppHeader back subtitle="Public runs — drop into someone else's story" />
      <main className="flex-1 w-full max-w-5xl mx-auto px-6 py-8">
        <h2 className="text-lg text-slate-300 mb-4">Public runs</h2>
        {loading ? (
          <p className="text-slate-500 text-sm">listening…</p>
        ) : active.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-700 p-6 text-slate-400 text-sm">
            No public runs are open right now.{' '}
            <Link to="/" className="text-amber-200/90 hover:underline">Start one →</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {active.map((run) => (
              <Link
                key={run.id}
                to={`/play/${run.id}`}
                className="block rounded-2xl border border-slate-800 bg-slate-900/40 p-5 transition-colors hover:border-slate-600"
              >
                <p className="text-xs uppercase tracking-widest text-amber-200/80 mb-0.5">{run.category}</p>
                <h3 className="text-xl text-slate-100">{run.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-snug">{run.blurb}</p>
                <div className="mt-3 flex items-center gap-3 text-[11px] text-slate-500">
                  <span>✦ scene {run.sceneCount}</span>
                  <span>· {run.participants?.length ?? 1} spirit{(run.participants?.length ?? 1) === 1 ? '' : 's'}</span>
                  <span className="text-amber-200/70">join →</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function tsMillis(ts: unknown): number {
  if (!ts) return 0;
  if (typeof ts === 'object' && ts && 'seconds' in (ts as Record<string, unknown>)) {
    return (ts as { seconds: number }).seconds * 1000;
  }
  return 0;
}
